// src/quiz/RosenbergTest.js
//
// Rosenberg Self-Esteem Scale (Rosenberg, 1965).
// Source: Rosenberg, M. (1965). Society and the adolescent self-image.
// Princeton, NJ: Princeton University Press.
// Items, response scale and scoring are reproduced unchanged. The scale may
// be used without explicit permission; the author's family (The Morris
// Rosenberg Foundation) asks to be kept informed of its use.

import React, { useState, useEffect } from "react";
import getApiBaseUrl from "../utils/api";
import { useNavigate } from "react-router-dom";

const TEST_NAME = "Rosenberg Self-Esteem Scale";

// reverse: true marks the asterisked items, which are reverse scored
const QUESTIONS = [
  { text: "On the whole, I am satisfied with myself.", reverse: false },
  { text: "At times, I think I am no good at all.", reverse: true },
  { text: "I feel that I have a number of good qualities.", reverse: false },
  { text: "I am able to do things as well as most other people.", reverse: false },
  { text: "I feel I do not have much to be proud of.", reverse: true },
  { text: "I certainly feel useless at times.", reverse: true },
  { text: "I feel that I’m a person of worth, at least on an equal plane with others.", reverse: false },
  { text: "I wish I could have more respect for myself.", reverse: true },
  { text: "All in all, I am inclined to feel that I am a failure.", reverse: true },
  { text: "I take a positive attitude toward myself.", reverse: false }
];

// SA=3, A=2, D=1, SD=0
const OPTIONS = [
  { label: "Strongly Agree", short: "SA", value: 3 },
  { label: "Agree", short: "A", value: 2 },
  { label: "Disagree", short: "D", value: 1 },
  { label: "Strongly Disagree", short: "SD", value: 0 }
];

const MAX_SCORE = 30;

// Rosenberg did not publish official cut-offs. Scores of 15–25 are commonly
// treated as the normal range, and below 15 as suggesting low self-esteem.
export const getRosenbergLevel = (score) =>
  score < 15
    ? "Low Self-Esteem"
    : score <= 25
    ? "Normal / Healthy Self-Esteem"
    : "High Self-Esteem";

export function RosenbergCredits() {
  return (
    <div style={styles.credits}>
      <h3 style={styles.creditsTitle}>Source & Credits</h3>
      <p>
        <strong>Rosenberg Self-Esteem Scale</strong> developed by Morris
        Rosenberg (1965). The original sample consisted of 5,024 high school
        juniors and seniors from 10 randomly selected schools in New York State.
      </p>
      <p>
        <strong>Source:</strong> Rosenberg, M. (1965). <em>Society and the
        adolescent self-image.</em> Princeton, NJ: Princeton University Press.
      </p>
      <p>
        <strong>Further references:</strong>
        <br />
        Crandal, R. (1973). The measurement of self-esteem and related
        constructs, pp. 80–82 in J.P. Robinson &amp; P.R. Shaver (Eds),{" "}
        <em>Measures of social psychological attitudes.</em> Revised edition.
        Ann Arbor: ISR.
        <br />
        Wylie, R. C. (1974). <em>The self-concept.</em> Revised edition.
        Lincoln, Nebraska: University of Nebraska Press.
      </p>
      <p>
        The scale may be used without explicit permission. With thanks to The
        Morris Rosenberg Foundation, c/o Department of Sociology, University of
        Maryland, College Park, MD 20742-1315.
      </p>
      <p>
        Score ranges shown in the result are a commonly used guideline, not
        official cut-offs from the original author.
      </p>
    </div>
  );
}

export default function RosenbergTest() {
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [resultData, setResultData] = useState(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const API_URL = getApiBaseUrl();
  const navigate = useNavigate();

  const handleChange = (index, value) => {
    const updated = [...answers];
    updated[index] = value;
    setAnswers(updated);
  };

  const calculateScore = async () => {
    if (answers.includes(null)) {
      alert("Please answer all questions before submitting");
      return;
    }

    // Reverse-scored items: SA=0, A=1, D=2, SD=3
    const score = answers.reduce(
      (sum, value, index) => sum + (QUESTIONS[index].reverse ? 3 - value : value),
      0
    );
    const level = getRosenbergLevel(score);
    const result = { score, level };

    try {
      const response = await fetch(`${API_URL}/tests/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ testName: TEST_NAME, result })
      });

      const savedData = await response.json();
      setResultData(savedData.result || result);
    } catch (error) {
      console.error("Error saving Rosenberg result:", error);
      setResultData(result);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= RESULT SCREEN ================= */

  if (resultData) {
    const interpretation =
      resultData.score < 15
        ? "Your score suggests you may be feeling low about yourself right now. This is common and can change — being kinder to yourself and talking with someone you trust can really help."
        : resultData.score <= 25
        ? "Your score is within the typical range. You generally see yourself in a balanced, healthy way."
        : "Your score suggests a strong, positive view of yourself. Keep nurturing the habits that support it.";

    return (
      <div style={styles.container}>
        <h1 style={styles.heading}>{TEST_NAME}</h1>

        <div style={styles.resultCard}>
          <h2>Your Self-Esteem Result</h2>

          <div style={styles.scoreText}>
            Score: {resultData.score} / {MAX_SCORE}
          </div>

          <div style={styles.levelText}>{resultData.level}</div>

          <p style={styles.interpretation}>{interpretation}</p>

          <p style={styles.disclaimer}>
            This scale is a self-reflection tool, not a diagnosis. If low
            feelings about yourself are affecting your daily life, consider
            speaking with a counsellor or mental health professional.
          </p>

          <div style={styles.savedMessage}>✅ Result saved to your profile!</div>

          <button onClick={() => navigate("/profile")} style={styles.button}>
            View Profile
          </button>
        </div>

        <RosenbergCredits />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Rosenberg Self-Esteem Scale</h1>

      <p style={styles.intro}>
        The Rosenberg Self-Esteem Scale is one of the most widely used measures of how you value and feel about yourself overall.
        <br />
        It has 10 short statements and takes about 2–3 minutes — answer honestly, as there are no right or wrong answers.
      </p>

      <p style={styles.description}>
        Below is a list of statements dealing with your general feelings about
        yourself. If you strongly agree, select <strong>SA</strong>. If you
        agree with the statement, select <strong>A</strong>. If you disagree,
        select <strong>D</strong>. If you strongly disagree, select{" "}
        <strong>SD</strong>.
      </p>

      {QUESTIONS.map((question, index) => (
        <div key={index} style={styles.questionCard}>
          <p style={styles.questionText}>
            {index + 1}. {question.text}
          </p>

          {OPTIONS.map((option) => (
            <label key={option.short} style={styles.optionLabel}>
              <input
                type="radio"
                name={`q${index}`}
                value={option.value}
                checked={answers[index] === option.value}
                onChange={() => handleChange(index, option.value)}
              />{" "}
              <strong>{option.short}</strong> — {option.label}
            </label>
          ))}
        </div>
      ))}

      <button style={styles.button} onClick={calculateScore}>
        Submit
      </button>

      <RosenbergCredits />
    </div>
  );
}

/* ================= STYLES ================= */

const styles = {
  container: {
    maxWidth: "800px",
    margin: "120px auto 60px",
    padding: "0 20px",
    textAlign: "center"
  },
  heading: {
    fontSize: "30px",
    color: "#19fd91",
    marginBottom: "20px"
  },
  intro: {
    color: "#19fd91",
    fontSize: "1rem",
    marginBottom: "15px",
    lineHeight: "1.6"
  },
  description: {
    color: "#ccc",
    fontSize: "0.95rem",
    marginBottom: "30px",
    lineHeight: "1.6"
  },
  questionCard: {
    background: "#111",
    border: "1px solid #19fd91",
    borderRadius: "12px",
    padding: "20px",
    marginBottom: "20px",
    textAlign: "left"
  },
  questionText: {
    fontWeight: "600"
  },
  optionLabel: {
    display: "block",
    marginTop: "8px",
    cursor: "pointer"
  },
  button: {
    padding: "12px 25px",
    background: "#19fd91",
    color: "#000",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "16px",
    marginTop: "20px"
  },
  resultCard: {
    background: "#111",
    border: "1px solid #19fd91",
    borderRadius: "12px",
    padding: "30px",
    marginTop: "20px"
  },
  scoreText: {
    fontSize: "1.3rem",
    fontWeight: "600",
    color: "#19fd91",
    marginTop: "10px"
  },
  levelText: {
    fontSize: "1.1rem",
    marginTop: "10px"
  },
  interpretation: {
    color: "#ddd",
    lineHeight: "1.6",
    marginTop: "15px"
  },
  disclaimer: {
    color: "#aaa",
    fontSize: "0.85rem",
    fontStyle: "italic",
    marginTop: "10px"
  },
  savedMessage: {
    marginTop: "15px",
    color: "#19fd91"
  },
  credits: {
    marginTop: "40px",
    padding: "20px",
    borderTop: "1px solid #233127",
    color: "#999",
    fontSize: "0.8rem",
    lineHeight: "1.6",
    textAlign: "left"
  },
  creditsTitle: {
    color: "#19fd91",
    fontSize: "1rem",
    marginBottom: "8px"
  }
};
